// framework imports - 1st party
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// lib imports - 3rd party
import { ToastContainer, Slide } from "react-toastify";

// app imports
import * as Auth from "../authService";
import * as notify from "../notify";
import {
  beginCognitoLogin,
  fetchIdpHint,
  fetchSsoConfig,
  forgetSsoEmail,
  isSsoConfigured,
  rememberedSsoEmail,
} from "../sso/cognitoAuth";

import Logo from "../icons/logo.png";
import "../stylesheets/Login.css";

// The IdP lookup only makes sense for email-style User IDs.
const looksLikeEmail = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);

// Login page.
// - SSO not enabled for the environment: the usual User ID + Password form.
// - SSO enabled: the same form, and while an email is typed the API says which IdP owns its domain. A mapped domain
//   hides the Password field and changes the button to "Continue with SSO", which goes straight to that IdP. If the
//   domain is not force-SSO, "Use a password instead" brings the Password field and Login button back.
// - SSO enabled, and this browser signed in with SSO before (also after a logout): the page offers
//   "Continue as <email>" and "Use a different account" instead of the form.

const Login = (props) => {
  const navigate = useNavigate();

  // if already logged in, go to main page
  useEffect(() => {
    if (Auth.loggedIn()) navigate("/summary", { replace: true });
  }, []);

  const [userID, setUserID] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState();
  const [disableLoginBtn, setDisableLoginBtn] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [ssoConfig, setSsoConfig] = useState(null);
  // Nothing is rendered until the config call finishes, so the password form does not flash and disappear.
  const [configLoaded, setConfigLoaded] = useState(false);
  // True once the user clicked "Use a password instead" for an email that has an IdP. Reset when the User ID changes.
  const [passwordChosen, setPasswordChosen] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  // Last resolved IdP lookup: { email, identityProvider, forceSso }. Only used for the exact email it was made for.
  const [hint, setHint] = useState(null);
  // Email of the last SSO sign-in in this browser (kept across logout). Only a shortcut; it is sent as a login hint.
  const [rememberedEmail, setRememberedEmail] = useState(() =>
    rememberedSsoEmail()
  );

  const ssoEnabled = isSsoConfigured(ssoConfig);
  const showContinueAs = ssoEnabled && rememberedEmail !== "";
  // The IdP lookup runs while the original form is shown, unless the user already chose the password.
  const lookupActive = ssoEnabled && !showContinueAs && !passwordChosen;
  const hintMatches = Boolean(hint && hint.email === userID.trim());
  // The IdP of the typed email. It is kept while the user has chosen a password, so "Use SSO instead" can bring SSO
  // back without another lookup.
  const ssoIdp =
    ssoEnabled && !showContinueAs && hintMatches ? hint.identityProvider : null;
  // SSO mode: the email has an IdP, so the Password field is hidden and the button reads "Continue with SSO".
  const ssoMode = Boolean(ssoIdp) && !passwordChosen;
  // A forced domain has no password route, so the "Use a password instead" link is hidden for it.
  const ssoForced = hintMatches ? Boolean(hint.forceSso) : false;

  // SSO settings are served by the API at runtime (one image is promoted through every environment).
  // If the call fails the page simply behaves as a password-only login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await fetchSsoConfig();
        if (cancelled) return;
        setSsoConfig(config);
      } catch (err) {
        // The page then behaves as a password-only login.
      }
      if (!cancelled) setConfigLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // While an email is typed, ask the API (after a short pause) which IdP owns its domain. The result only
  // changes the form; it is never trusted for a different text than it was resolved for.
  useEffect(() => {
    const email = userID.trim();
    // A chosen password keeps the stored result (and asks for no new one) so the way back to SSO stays available.
    if (passwordChosen && ssoEnabled && !showContinueAs) {
      return undefined;
    }
    if (!lookupActive || !looksLikeEmail(email)) {
      setHint(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchIdpHint(email)
        .then((res) => {
          if (!cancelled) {
            setHint({
              email,
              identityProvider: res.identityProvider || null,
              forceSso: Boolean(res.forceSso),
            });
          }
        })
        .catch(() => {
          if (!cancelled) setHint(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userID, lookupActive, passwordChosen, ssoEnabled, showContinueAs]);

  // Redirect to the IdP; the browser leaves the page, so the button stays disabled on success.
  const startSso = async (identityProvider, email = userID.trim()) => {
    try {
      // The email goes to the IdP as a login hint and is checked again when the sign-in returns.
      await beginCognitoLogin(ssoConfig, identityProvider, { email });
    } catch (err) {
      setSsoBusy(false);
      notify.error(err.message || "Failed to start SSO");
    }
  };

  // "Continue as <email>": look up the IdP for the remembered address and go straight to it. If the lookup says
  // the domain no longer has an IdP, drop the shortcut and show the normal form with the address filled in.
  const handleContinueAs = async () => {
    const email = rememberedEmail;
    setErrorMsg(null);
    setSsoBusy(true);
    let result = null;
    try {
      result = await fetchIdpHint(email);
    } catch (err) {
      result = null;
    }
    if (result && result.identityProvider) {
      await startSso(result.identityProvider, email);
      return;
    }
    setSsoBusy(false);
    if (result) forgetSsoEmail();
    setRememberedEmail("");
    setUserID(email);
  };

  const handleUseDifferentAccount = (e) => {
    e.preventDefault();
    forgetSsoEmail();
    setRememberedEmail("");
    setErrorMsg(null);
  };

  // Editing the User ID starts over, so a stale lookup or password choice is never reused for a different ID.
  const handleUserIdChange = (e) => {
    setUserID(e.target.value);
    if (passwordChosen) {
      setPasswordChosen(false);
      setPassword("");
      setErrorMsg(null);
    }
  };

  // Fallback for a domain that has an IdP but is not forced: skip SSO and sign in with a password.
  const handleUsePassword = (e) => {
    e.preventDefault();
    setErrorMsg(null);
    setPasswordChosen(true);
  };

  // Way back from the password view to SSO for the same email.
  const handleUseSso = (e) => {
    e.preventDefault();
    setPasswordChosen(false);
    setPassword("");
    setErrorMsg(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setErrorMsg(null);

    if (showContinueAs) {
      handleContinueAs();
      return;
    }

    // The email has an IdP: Enter and the button both go to SSO.
    if (ssoMode) {
      setSsoBusy(true);
      await startSso(ssoIdp);
      return;
    }

    // An email with no lookup result yet (Enter before the pause ended, or a failed lookup): ask the API now.
    const id = userID.trim();
    if (lookupActive && looksLikeEmail(id) && !hintMatches) {
      setSsoBusy(true);
      let result = null;
      try {
        result = await fetchIdpHint(id);
      } catch (err) {
        result = null;
      }
      if (result && result.identityProvider) {
        await startSso(result.identityProvider);
        return;
      }
      setSsoBusy(false);
      if (result && result.forceSso) {
        // The domain requires SSO but has no IdP configured, so a password cannot be used either.
        setErrorMsg("SSO is required for this account.");
        return;
      }
    }

    // if username and password are filled in
    if (userID && password) {
      Auth.login(userID, password)
        .then((res) => {
          if (res.status === 401) {
            // add input error message
            if (res.data.message === "Unauthorized") {
              setErrorMsg("Incorrect Username or Password.");
            } else if (res.data.message === "SSO required for this account") {
              setErrorMsg("SSO is required for this account.");
            } else {
              let data = JSON.parse(res.data.message);
              if (
                !data.numberOfLoginRemaining ||
                data.numberOfLoginRemaining === 0
              ) {
                setTimeRemaining(data.lockTimeRemaining);
                setDisableLoginBtn(true);
              } else {
                setErrorMsg("Incorrect Username or Password.");
              }
            }
          } else {
            if (Auth.isForceChangePassword() === "true") {
              navigate("/changepasswordpage", { replace: true });
            } else {
              navigate("/summary", { replace: true });
            }
          }
        })
        .catch((err) => {
          notify.error("Failed to login");
        });
    } else if (!userID && !password) {
      notify.error("Please enter a User ID and Password");
    } else if (!userID) {
      notify.error("Please enter a User ID");
    } else if (!password) {
      notify.error("Please enter a Password");
    }
  };

  // const calTimeRemaining = () => {
  //   let mins = Math.floor(timeRemaining / 60);
  //   let secs = timeRemaining - mins * 60;
  //
  //   if (mins.toString().length === 1) {
  //     mins = "0" + mins;
  //   }
  //
  //   if (secs.toString().length === 1) {
  //     secs = "0" + secs;
  //   }
  //
  //   return mins + ":" + secs + "s.";
  // };

  useEffect(() => {
    if (timeRemaining === 0 || timeRemaining === null) {
      setErrorMsg(null);
      setDisableLoginBtn(false);
      return;
    }

    setErrorMsg("Incorrect Username or Password.");

    const timer = setInterval(() => {
      setTimeRemaining(timeRemaining - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  return (
    <div className="center">
      <div className="card">
        <div id="LoginLogo">
          <div className="logo-wrapper">
            <img src={Logo} alt="Login Logo" />
          </div>
        </div>
        {configLoaded ? (
          <form onSubmit={handleFormSubmit}>
            {showContinueAs ? null : <label htmlFor="userID">User ID</label>}
            {showContinueAs ? null : (
              <input
                className={"proview-input login"}
                placeholder="User ID"
                id="userID"
                name="userID"
                type="text"
                autoCapitalize="off"
                autoComplete="off"
                spellCheck="false"
                autoCorrect="off"
                value={userID}
                onChange={handleUserIdChange}
              />
            )}
            {showContinueAs || ssoMode ? null : (
              <>
                <label htmlFor="password">Password</label>
                <input
                  className={"proview-input login"}
                  placeholder="Password"
                  id="password"
                  name="password"
                  type="password"
                  autoCapitalize="off"
                  autoComplete="off"
                  spellCheck="false"
                  autoCorrect="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </>
            )}
            {errorMsg ? <div className="input-error">{errorMsg}</div> : null}

            {showContinueAs ? (
              <button
                className="proview-button login"
                type="submit"
                id="continueAsButton"
                disabled={ssoBusy}
              >
                {ssoBusy ? "Please wait…" : `Continue as ${rememberedEmail}`}
              </button>
            ) : ssoMode ? (
              <button
                className="proview-button login"
                type="submit"
                id="continueButton"
                disabled={ssoBusy}
              >
                {ssoBusy ? "Please wait…" : "Continue with SSO"}
              </button>
            ) : (
              <button
                className="proview-button login"
                type="submit"
                id="loginButton"
                disabled={disableLoginBtn || ssoBusy}
              >
                Login
              </button>
            )}
            {showContinueAs && !ssoBusy ? (
              <a
                href="#different"
                className="login-back-link"
                id="useDifferentAccountLink"
                onClick={handleUseDifferentAccount}
              >
                Use a different account
              </a>
            ) : null}
            {ssoMode && !ssoForced && !ssoBusy ? (
              <a
                href="#password"
                className="login-back-link"
                id="usePasswordLink"
                onClick={handleUsePassword}
              >
                Use a password instead
              </a>
            ) : null}
            {passwordChosen && ssoIdp && !ssoBusy ? (
              <a
                href="#sso"
                className="login-back-link"
                id="useSsoLink"
                onClick={handleUseSso}
              >
                Use SSO instead
              </a>
            ) : null}
          </form>
        ) : null}
      </div>
      <ToastContainer transition={Slide} />
    </div>
  );
};

export default Login;
